import React from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'

const eurFormatter = new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' })
const numFormatter = new Intl.NumberFormat('fi-FI', { maximumFractionDigits: 1 })

const numCellSx = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' }

// rows: [{ weekLabel, dateRange, sales, occupancy, windowLength }]
const WeeklyBreakdownTable = ({ title = 'Viikoittaiset luvut', rows, loading }) => {
  const safe = Array.isArray(rows) ? rows : []
  const totals = safe.reduce(
    (acc, r) => {
      acc.sales += Number(r.sales) || 0
      acc.occupancy += Number(r.occupancy) || 0
      acc.windowLength += Number(r.windowLength) || 0
      return acc
    },
    { sales: 0, occupancy: 0, windowLength: 0 }
  )
  const totalPercent = totals.windowLength > 0 ? (totals.occupancy / totals.windowLength) * 100 : null

  return (
    <Card sx={{ borderRadius: 2, height: '100%' }}>
      <CardContent sx={{ p: '12px 14px', '&:last-child': { pb: '12px' } }}>
        <Typography variant='caption' sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3 }}>
          {title}
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180 }}>
            <CircularProgress size={20} />
          </Box>
        ) : safe.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180, color: 'text.secondary', fontSize: 12 }}>
            Ei dataa
          </Box>
        ) : (
          <Table size='small' sx={{ mt: 1, '& td, & th': { px: 1, py: 0.5, fontSize: 12 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Viikko</TableCell>
                <TableCell sx={numCellSx}>Myynti</TableCell>
                <TableCell sx={numCellSx}>Tunnit varattu</TableCell>
                <TableCell sx={numCellSx}>Vapaat tunnit</TableCell>
                <TableCell sx={numCellSx}>Varausaste</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {safe.map((r) => {
                const pct = r.windowLength > 0 ? (r.occupancy / r.windowLength) * 100 : null
                return (
                  <TableRow key={r.weekLabel + r.dateRange}>
                    <TableCell>
                      {r.weekLabel} <Box component='span' sx={{ color: 'text.secondary' }}>{r.dateRange}</Box>
                    </TableCell>
                    <TableCell sx={numCellSx}>{eurFormatter.format(r.sales || 0)}</TableCell>
                    <TableCell sx={numCellSx}>{numFormatter.format(r.occupancy || 0)}</TableCell>
                    <TableCell sx={numCellSx}>{numFormatter.format((r.windowLength || 0) - (r.occupancy || 0))}</TableCell>
                    <TableCell sx={numCellSx}>{pct === null ? '—' : numFormatter.format(pct) + ' %'}</TableCell>
                  </TableRow>
                )
              })}
              <TableRow sx={{ '& td': { fontWeight: 600, borderTop: '1px solid #e0e0e0' } }}>
                <TableCell>Yhteensä</TableCell>
                <TableCell sx={numCellSx}>{eurFormatter.format(totals.sales)}</TableCell>
                <TableCell sx={numCellSx}>{numFormatter.format(totals.occupancy)}</TableCell>
                <TableCell sx={numCellSx}>{numFormatter.format(totals.windowLength - totals.occupancy)}</TableCell>
                <TableCell sx={numCellSx}>{totalPercent === null ? '—' : numFormatter.format(totalPercent) + ' %'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

export default WeeklyBreakdownTable
